import { useApolloClient, type ApolloError } from '@apollo/client';
import {
  useGetMyProfile,
  useLogin,
  useLogout,
  type ClientUser,
  type LoginInput,
} from '@exam-room/graphql';
import * as Sentry from '@sentry/react';
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';

import { LoadingSpinner } from '../../components';

import { AuthState, type AuthAction, type AuthStateShape, type Auth } from './types';
import { mapUserProfile } from './utils';

function authReducer(state: AuthStateShape, action: AuthAction): AuthStateShape {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, authState: AuthState.LOADING };
    case 'SET_LOGGED_IN':
      return { authState: AuthState.LOGGED_IN, user: action.payload };
    case 'SET_NOT_LOGGED_IN':
      return { authState: AuthState.NOT_LOGGED_IN, user: null };
    default:
      return state;
  }
}

const AuthContext = createContext<Auth | undefined>(undefined);

function AuthProvider({ children }: { children?: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, {
    authState: AuthState.LOADING,
    user: null,
  });

  const client = useApolloClient();

  const [logoutMutation, { loading: isLoggingOut }] = useLogout();

  const [loginMutation, { loading: isLoggingIn }] = useLogin({});

  const { loading: myProfileLoading, refetch: refetchMyProfile } = useGetMyProfile({
    onCompleted: (data) => {
      if (data?.cluGetMyProfile) {
        document.title = `SET Exam Room - ${data?.cluGetMyProfile.firstname} ${data?.cluGetMyProfile.lastname}`;
        dispatch({
          type: 'SET_LOGGED_IN',
          payload: mapUserProfile(data.cluGetMyProfile as ClientUser),
        });
      } else {
        dispatch({ type: 'SET_NOT_LOGGED_IN' });
      }
    },
    onError: () => {
      dispatch({ type: 'SET_NOT_LOGGED_IN' });
    },
    skip: state.authState === AuthState.NOT_LOGGED_IN,
  });

  const logout = useCallback(async () => {
    await logoutMutation({
      onCompleted: async () => {
        await client.resetStore();
        client.cache.modify({
          fields: {
            getMyProfile: () => null,
          },
        });
        dispatch({ type: 'SET_NOT_LOGGED_IN' });
      },
      onError: (error: ApolloError) => console.error(error),
    });
  }, [client]);

  const login = useCallback(async (input: LoginInput) => {
    const { data } = await loginMutation({
      variables: { input },
      onError: (error: ApolloError) => console.error(error),
      onCompleted: (data) => {
        if (data.erLogin.success) {
          refetchMyProfile();
        }
      },
    });

    return data?.erLogin;
  }, []);

  const contextValue = useMemo(
    () => ({
      authState: state.authState,
      user: state.user,
      logout,
      login,
      isLoading: isLoggingIn || isLoggingOut || myProfileLoading,
    }),
    [state, logout, login, isLoggingIn, isLoggingOut, myProfileLoading],
  );

  useEffect(() => {
    if (!Sentry.isInitialized()) return;

    if (state.user) {
      Sentry.setUser({ username: state.user.username, email: state.user.email ?? undefined });
      Sentry.setContext('User Profile', {
        username: state.user.username,
        firstname: state.user.firstname,
        lastname: state.user.lastname,
        displayName: state.user.displayName,
        email: state.user.email,
      });
    } else {
      Sentry.setUser(null);
      Sentry.setContext('User Profile', null);
    }
  }, [state.user]);

  if (state.authState === AuthState.LOADING) {
    return (
      <div className='grid h-dvh w-screen place-items-center'>
        <LoadingSpinner />
      </div>
    );
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

function useAuth(): Auth {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export { AuthContext, AuthState, useAuth, type Auth, type ClientUser };
export default AuthProvider;
